/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * ActivityPicker — narrows the History tab to one activity of an event.
 *
 * NOT RENDERED FOR A SINGLE-ACTIVITY EVENT. With one activity, "All
 * activities" and that activity are the same set: there is nothing to choose
 * between, and a disabled control would be dead furniture inviting a click
 * that does nothing. Callers check `hasChoice` rather than this deciding for
 * itself, so the bar's layout does not depend on a child's internals.
 *
 * Each option carries its own capacity, because that is the number an
 * organizer is usually hunting when they open this list at all.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';

export const ALL_ACTIVITIES = 'all';

/** True when this event has enough activities for the choice to mean anything. */
export function hasChoice(activities) {
  return (activities || []).length > 1;
}

const placesLabel = (capacity) => (capacity == null ? 'No cap' : capacity.toLocaleString());

function ActivityPicker({ activities, selected, onSelect, totalCapacity }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const options = useMemo(() => ([
    { id: ALL_ACTIVITIES, name: 'All activities', capacity: totalCapacity },
    ...activities.map((activity) => ({
      id: activity.id, name: activity.name, capacity: activity.capacity,
    })),
  ]), [activities, totalCapacity]);

  const current = options.find((option) => option.id === selected) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    const onClick = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  // Arrow keys move between options, matching the event picker beside it.
  const onListKey = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = [...(boxRef.current?.querySelectorAll('.ocha-option') || [])];
    if (!items.length) return;
    event.preventDefault();
    const at = items.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown'
      ? Math.min(at + 1, items.length - 1)
      : Math.max(at - 1, 0);
    items[at === -1 ? 0 : next].focus();
  };

  const choose = (id) => { onSelect(id); setOpen(false); };

  return (
    <div className="ocha" ref={boxRef} onKeyDown={onListKey}>
      <button
        type="button"
        className="ocha-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Activity: ${current.name}`}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="ocha-main">
          <span className="ocha-lab">Activity</span>
          <b>{current.name}</b>
        </span>
        <span className="ocha-cap">{placesLabel(current.capacity)}</span>
        <i className={`bi bi-chevron-down ocha-chev${open ? ' is-open' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <ul className="ocha-menu" role="listbox" aria-label="Choose an activity">
          {options.map((option) => {
            const isOn = option.id === current.id;
            return (
              <li key={option.id} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isOn}
                  className={`ocha-option${isOn ? ' is-on' : ''}`}
                  onClick={() => choose(option.id)}
                >
                  <i className={`bi ${isOn ? 'bi-check2' : ''} ocha-tick`} aria-hidden="true" />
                  <span className="ocha-name">{option.name}</span>
                  <span className="ocha-pill">{placesLabel(option.capacity)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default ActivityPicker;
